import {
  deleteSelectedElements,
  handleGroupEditingState,
} from "../actions/actionDeleteSelected";
import { fixBindingsAfterDeletion } from "./binding";
import { newElementWith } from "./mutateElement";
import { updateActiveTool } from "../utils";
import { ExcalidrawElement, ExcalidrawLinearElement } from "./types";
import { AppState } from "../types";

const isArrow = (
  element: ExcalidrawElement,
): element is ExcalidrawLinearElement => element.type === "arrow";

/** An arrow's bindings, read BEFORE deletion severs them. */
type ArrowBindings = Pick<
  ExcalidrawLinearElement,
  "startBinding" | "endBinding"
>;

/**
 * Swap the shape the user right-clicked for a freshly built integration element:
 * delete the original, give the replacement the original's bounds, and transplant
 * every arrow bound to the original onto it.
 *
 * Returns the whole next scene so the caller commits creation and replacement
 * together. It used to be two commits a `setTimeout` apart — the element was
 * appended, and 300-400ms later a second pass in the app layer went looking for
 * both halves and finished the job. That window was visible (both shapes on
 * canvas at once) and the delay was a guess; worse, the second pass had to
 * re-find what the first had just made. Identifying either half by anything but
 * its id has burned this flow twice: positionally out of the scratch localStorage
 * snapshot, which a cloud-backed scene never writes, and as "the newest
 * notion-ish element", which is some other map's pill whenever nothing was
 * dropped or a remote element landed inside the window.
 *
 * Null when the target is gone, which leaves the caller to drop the element
 * without replacing anything rather than throw.
 */
export const replaceElementWithIntegration = ({
  elements,
  appState,
  targetId,
  integration,
}: {
  elements: readonly ExcalidrawElement[];
  appState: AppState;
  targetId: string;
  integration: ExcalidrawElement;
}): { elements: ExcalidrawElement[]; appState: AppState } | null => {
  const target = elements.find(
    (element) => element.id === targetId && !element.isDeleted,
  );
  if (!target || targetId === integration.id) {
    return null;
  }

  const scene = [...elements, integration];

  // Deletion severs the bindings in place, so read them first.
  const bindingsBefore = new Map<string, ArrowBindings>(
    scene
      .filter(isArrow)
      .filter(
        (arrow) =>
          arrow.startBinding?.elementId === targetId ||
          arrow.endBinding?.elementId === targetId,
      )
      .map((arrow) => [
        arrow.id,
        { startBinding: arrow.startBinding, endBinding: arrow.endBinding },
      ]),
  );

  // Delete the target explicitly rather than trusting the live selection, which
  // by now belongs to the element being dropped. Still via `deleteSelectedElements`
  // so bound text, frame children and mindmap subtrees are handled as usual.
  const { elements: deletedScene, appState: afterDelete } =
    deleteSelectedElements(scene, {
      ...appState,
      selectedElementIds: { [targetId]: true },
    });
  fixBindingsAfterDeletion(deletedScene, [target]);

  const survivingArrowIds = new Set(
    deletedScene
      .filter((element) => !element.isDeleted && isArrow(element))
      .map((element) => element.id),
  );

  const next = deletedScene.map((element) => {
    if (element.id === integration.id) {
      return newElementWith(element, {
        width: target.width,
        height: target.height,
        // Only bindings that outlive the replacement: the target's bound text
        // is deleted with it, and carrying it over would dangle.
        boundElements:
          target.boundElements?.filter((bound) =>
            survivingArrowIds.has(bound.id),
          ) ?? null,
      });
    }
    const bindings = bindingsBefore.get(element.id);
    if (!bindings || element.isDeleted || !isArrow(element)) {
      return element;
    }
    return newElementWith(element, {
      startBinding:
        bindings.startBinding?.elementId === targetId
          ? { ...bindings.startBinding, elementId: integration.id }
          : bindings.startBinding,
      endBinding:
        bindings.endBinding?.elementId === targetId
          ? { ...bindings.endBinding, elementId: integration.id }
          : bindings.endBinding,
    });
  });

  return {
    elements: next,
    appState: {
      ...handleGroupEditingState(afterDelete, next),
      activeTool: updateActiveTool(appState, { type: "selection" }),
      multiElement: null,
      activeEmbeddable: null,
    },
  };
};
